// src/components/profile-input/steps/EducationProfessionStep.jsx
import React, { useState, useEffect, useRef } from "react";
import { Grid, Typography, Divider, MenuItem, Select, InputAdornment } from "@mui/material";
import { TextInput } from "../FormFields";
import { options } from "../formUtils";

const EducationProfessionStep = ({
  formData,
  handleInputChange,
  errors = {}
}) => {
  // State for smart dropdown fields
  const [religionInput, setReligionInput] = useState("");
  const [showReligionOptions, setShowReligionOptions] = useState(false);

  const [employmentInput, setEmploymentInput] = useState("");
  const [showEmploymentOptions, setShowEmploymentOptions] = useState(false);

  const [practiceLevelInput, setPracticeLevelInput] = useState("");
  const [showPracticeLevelOptions, setShowPracticeLevelOptions] = useState(false);

  const [maritalStatusInput, setMaritalStatusInput] = useState("");
  const [showMaritalStatusOptions, setShowMaritalStatusOptions] = useState(false);

  const [sectInput, setSectInput] = useState("");
  const [showSectOptions, setShowSectOptions] = useState(false);

  const [casteInput, setCasteInput] = useState("");
  const [showCasteOptions, setShowCasteOptions] = useState(false);

  const [degreeInput, setDegreeInput] = useState("");
  const [showDegreeOptions, setShowDegreeOptions] = useState(false);

  const [otherQualInput, setOtherQualInput] = useState("");
  
  // State for currency selection
  const [incomeCurrency, setIncomeCurrency] = useState("PKR");

  // Refs for dropdown containers to handle click outside
  const religionRef = useRef(null);
  const employmentRef = useRef(null);
  const practiceLevelRef = useRef(null);
  const maritalStatusRef = useRef(null);
  const sectRef = useRef(null);
  const casteRef = useRef(null);
  const degreeRef = useRef(null);

  // Initialize from form data
  useEffect(() => {
    setReligionInput(formData.religious_details?.religion || "");
    setEmploymentInput(formData.profession?.employment_type || "");
    setPracticeLevelInput(formData.religious_details?.religious_practice_level || "");
    setMaritalStatusInput(formData.marital_details?.marital_status || "");
    setSectInput(formData.religious_details?.sect || "");
    setCasteInput(formData.religious_details?.caste || "");
    setDegreeInput(formData.education?.highest_degree || "");
  }, [
    formData.religious_details?.religion,
    formData.profession?.employment_type,
    formData.religious_details?.religious_practice_level,
    formData.marital_details?.marital_status,
    formData.religious_details?.sect,
    formData.religious_details?.caste,
    formData.education?.highest_degree
  ]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdowns = [
        { ref: religionRef, setter: setShowReligionOptions },
        { ref: employmentRef, setter: setShowEmploymentOptions },
        { ref: practiceLevelRef, setter: setShowPracticeLevelOptions },
        { ref: maritalStatusRef, setter: setShowMaritalStatusOptions },
        { ref: sectRef, setter: setShowSectOptions },
        { ref: casteRef, setter: setShowCasteOptions },
        { ref: degreeRef, setter: setShowDegreeOptions }
      ];

      dropdowns.forEach(({ ref, setter }) => {
        if (ref.current && !ref.current.contains(event.target)) {
          setter(false);
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle qualification add
  const handleAddQualification = () => {
    if (otherQualInput.trim()) {
      const currentQualifications = formData.education?.other_qualifications || [];
      handleInputChange("education.other_qualifications", [...currentQualifications, otherQualInput.trim()]);
      setOtherQualInput("");
    }
  };

  const handleRemoveQualification = (index) => {
    const currentQualifications = formData.education?.other_qualifications || [];
    const newQualifications = currentQualifications.filter((_, i) => i !== index);
    handleInputChange("education.other_qualifications", newQualifications);
  };

  // Handle income change
  const handleIncomeChange = (e) => {
    const value = e.target.value;
    const formattedValue = incomeCurrency === "PKR" ? parseInt(value) || 0 : `${value} ${incomeCurrency}`;
    handleInputChange("profession.monthly_income", formattedValue);
  };

  // Handle currency change
  const handleCurrencyChange = (e) => {
    const newCurrency = e.target.value;
    setIncomeCurrency(newCurrency);
    
    const currentValue = formData.profession?.monthly_income;
    if (currentValue && typeof currentValue === 'string' && currentValue.includes(' ')) {
      const numberPart = currentValue.split(' ')[0];
      handleInputChange("profession.monthly_income", `${numberPart} ${newCurrency}`);
    } else if (currentValue) {
      handleInputChange("profession.monthly_income", `${currentValue} ${newCurrency}`);
    }
  };

  // Get income value for display
  const getIncomeValueForDisplay = () => {
    const incomeValue = formData.profession?.monthly_income;
    if (!incomeValue) return "";
    
    if (typeof incomeValue === 'string' && incomeValue.includes(' ')) {
      return incomeValue.split(' ')[0];
    }
    return incomeValue.toString();
  };

  // ========== SMART DROPDOWN HANDLERS ==========

  // Generic handler for smart dropdowns
  const createSmartDropdownHandlers = (field, setInputFn, setShowOptionsFn) => ({
    handleInputChange: (e) => {
      const value = e.target.value;
      setInputFn(value);
      setShowOptionsFn(true);
      handleInputChange(field, value);
    },
    handleFocus: () => {
      setShowOptionsFn(true);
    },
    handleSelect: (value) => {
      setInputFn(value);
      handleInputChange(field, value);
      setShowOptionsFn(false);
    }
  });

  // Religion handlers
  const religionHandlers = createSmartDropdownHandlers(
    "religious_details.religion",
    setReligionInput,
    setShowReligionOptions
  );

  // Employment type handlers
  const employmentHandlers = createSmartDropdownHandlers(
    "profession.employment_type",
    setEmploymentInput,
    setShowEmploymentOptions
  );

  // Practice level handlers
  const practiceLevelHandlers = createSmartDropdownHandlers(
    "religious_details.religious_practice_level",
    setPracticeLevelInput,
    setShowPracticeLevelOptions
  );

  // Marital status handlers
  const maritalStatusHandlers = createSmartDropdownHandlers(
    "marital_details.marital_status",
    setMaritalStatusInput,
    setShowMaritalStatusOptions
  );

  // Sect handlers
  const sectHandlers = createSmartDropdownHandlers(
    "religious_details.sect",
    setSectInput,
    setShowSectOptions
  );

  // Caste handlers
  const casteHandlers = createSmartDropdownHandlers(
    "religious_details.caste",
    setCasteInput,
    setShowCasteOptions
  );

  // Degree handlers
  const degreeHandlers = createSmartDropdownHandlers(
    "education.highest_degree",
    setDegreeInput,
    setShowDegreeOptions
  );

  // ========== FILTER OPTIONS ==========
  const filteredReligionOptions = options.religions.filter(option =>
    option.toLowerCase().includes(religionInput.toLowerCase())
  );

  const filteredEmploymentOptions = options.employment_types.filter(option =>
    option.toLowerCase().includes(employmentInput.toLowerCase())
  );

  const filteredPracticeLevelOptions = options.practice_levels.filter(option =>
    option.toLowerCase().includes(practiceLevelInput.toLowerCase())
  );

  const filteredMaritalStatusOptions = options.marital_statuses.filter(option =>
    option.toLowerCase().includes(maritalStatusInput.toLowerCase())
  );

  // Get sect options based on selected religion
  const getSectOptions = () => {
    const religion = formData.religious_details?.religion || "Islam";
    return options.sects[religion] || options.sects.Other || [];
  };

  // Get caste options based on selected religion
  const getCasteOptions = () => {
    const religion = formData.religious_details?.religion || "Islam";
    return options.castes[religion] || options.castes.Other || [];
  };

  const filteredSectOptions = getSectOptions().filter(option =>
    option.toLowerCase().includes(sectInput.toLowerCase())
  );

  const filteredCasteOptions = getCasteOptions().filter(option =>
    option.toLowerCase().includes(casteInput.toLowerCase())
  );

  const filteredDegreeOptions = options.education_levels.filter(option =>
    option.toLowerCase().includes(degreeInput.toLowerCase())
  );

  // ========== RENDER SMART DROPDOWN COMPONENT ==========
  const renderSmartDropdown = (label, value, handlers, filteredOptions, showOptions, setShowOptionsFn, containerRef, placeholder = "Type or select from options", error = false, helperText = "") => (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <TextInput
        label={label}
        value={value}
        onChange={handlers.handleInputChange}
        onFocus={handlers.handleFocus}
        placeholder={placeholder}
        fullWidth
        error={error}
        helperText={helperText}
      />
      {showOptions && filteredOptions.length > 0 && (
        <div 
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            zIndex: 1000,
            maxHeight: '200px',
            overflowY: 'auto',
            marginTop: '2px'
          }}
        >
          {filteredOptions.map((option, index) => (
            <div
              key={`${option}-${index}`}
              style={{
                padding: '8px 16px',
                cursor: 'pointer',
                borderBottom: '1px solid #eee',
                fontSize: '14px'
              }}
              onClick={() => handlers.handleSelect(option)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Grid container spacing={3}>
      {/* Religious & Marital Details Section */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom color="primary">
          Religious & Marital Details
        </Typography>
        <Divider sx={{ mb: 3 }} />
      </Grid>

      {/* Religion Field */}
      <Grid item xs={12} sm={6}>
        {renderSmartDropdown(
          "Religion *",
          religionInput,
          religionHandlers,
          filteredReligionOptions,
          showReligionOptions,
          setShowReligionOptions,
          religionRef,
          "Type or select religion"
        )}
      </Grid>

      {/* Sect Field */}
      <Grid item xs={12} sm={6}>
        {renderSmartDropdown(
          "Sect",
          sectInput,
          sectHandlers,
          filteredSectOptions,
          showSectOptions,
          setShowSectOptions,
          sectRef,
          "Type or select sect"
        )}
      </Grid>

      {/* Caste Field */}
      <Grid item xs={12} sm={6}>
        {renderSmartDropdown(
          "Caste *",
          casteInput,
          casteHandlers,
          filteredCasteOptions,
          showCasteOptions,
          setShowCasteOptions,
          casteRef,
          "Type or select caste"
        )}
      </Grid>

      {/* Practice Level Field */}
      <Grid item xs={12} sm={6}>
        {renderSmartDropdown(
          "Religious Practice Level",
          practiceLevelInput,
          practiceLevelHandlers,
          filteredPracticeLevelOptions,
          showPracticeLevelOptions,
          setShowPracticeLevelOptions,
          practiceLevelRef,
          "Type or select practice level"
        )}
      </Grid>

      {/* Marital Status Field */}
      <Grid item xs={12} sm={6}>
        {renderSmartDropdown(
          "Marital Status *",
          maritalStatusInput,
          maritalStatusHandlers,
          filteredMaritalStatusOptions,
          showMaritalStatusOptions,
          setShowMaritalStatusOptions,
          maritalStatusRef,
          "Type or select marital status"
        )}
      </Grid>

      {/* Children Field */}
      <Grid item xs={12} sm={6}>
        <TextInput
          label="Number of Children"
          value={formData.marital_details?.children || ""}
          onChange={(e) => handleInputChange("marital_details.children", parseInt(e.target.value) || 0)}
          type="number"
          InputProps={{ inputProps: { min: 0 } }}
          fullWidth
        />
      </Grid>

      {/* Education Section */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
          Education
        </Typography>
        <Divider sx={{ mb: 3 }} />
      </Grid>

      {/* Highest Degree Field */}
      <Grid item xs={12} sm={6}>
        {renderSmartDropdown(
          "Highest Degree *",
          degreeInput,
          degreeHandlers,
          filteredDegreeOptions,
          showDegreeOptions,
          setShowDegreeOptions,
          degreeRef,
          "Type or select highest degree",
          !!errors.highest_degree,
          errors.highest_degree
        )}
      </Grid>

      {/* Degree Field - Text input for specific degree name */}
      <Grid item xs={12} sm={6}>
        <TextInput
          label="Degree"
          value={formData.education?.degree || ""}
          onChange={(e) => handleInputChange("education.degree", e.target.value)}
          placeholder="e.g., BSc Computer Science, MBA, etc."
          fullWidth
        />
      </Grid>

      {/* Institution Field */}
      <Grid item xs={12} sm={6}>
        <TextInput
          label="Institution"
          value={formData.education?.institution || ""}
          onChange={(e) => handleInputChange("education.institution", e.target.value)}
          fullWidth
        />
      </Grid>

      {/* Other Qualifications - Custom Array Input */}
      <Grid item xs={12}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8} md={9}>
            <TextInput
              label="Add Other Qualification"
              value={otherQualInput}
              onChange={(e) => setOtherQualInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddQualification()}
              fullWidth
              placeholder="Type a qualification and press Add or Enter"
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <button
              onClick={handleAddQualification}
              disabled={!otherQualInput.trim()}
              style={{
                padding: '8px 16px',
                backgroundColor: otherQualInput.trim() ? '#1976d2' : '#cccccc',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: otherQualInput.trim() ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: '500',
                width: '100%',
                height: '56px',
                marginTop: '8px'
              }}
            >
              Add
            </button>
          </Grid>
        </Grid>

        {/* Display added qualifications as chips */}
        {formData.education?.other_qualifications && formData.education.other_qualifications.length > 0 && (
          <Grid item xs={12} sx={{ mt: 2 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {formData.education.other_qualifications.map((qualification, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    padding: '6px 12px',
                    borderRadius: '16px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span>{qualification}</span>
                  <button
                    onClick={() => handleRemoveQualification(index)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#f44336',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '0',
                      lineHeight: '1'
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </Grid>
        )}
      </Grid>

      {/* Profession Section */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
          Profession
        </Typography>
        <Divider sx={{ mb: 3 }} />
      </Grid>

      {/* Job Title Field */}
      <Grid item xs={12} sm={6}>
        <TextInput
          label="Job Title"
          value={formData.profession?.job_title || ""}
          onChange={(e) => handleInputChange("profession.job_title", e.target.value)}
          required
          error={!!errors.job_title}
          helperText={errors.job_title}
          fullWidth
        />
      </Grid>

      {/* Organization Field */}
      <Grid item xs={12} sm={6}>
        <TextInput
          label="Organization"
          value={formData.profession?.organization || ""}
          onChange={(e) => handleInputChange("profession.organization", e.target.value)}
          fullWidth
        />
      </Grid>

      {/* Employment Type Field */}
      <Grid item xs={12} sm={6}>
        {renderSmartDropdown(
          "Employment Type",
          employmentInput,
          employmentHandlers,
          filteredEmploymentOptions,
          showEmploymentOptions,
          setShowEmploymentOptions,
          employmentRef,
          "Type or select employment type"
        )}
      </Grid>

      {/* Monthly Income Field with Currency Selector */}
      <Grid item xs={12} sm={6}>
        <TextInput
          label="Monthly Income"
          value={getIncomeValueForDisplay()}
          onChange={handleIncomeChange}
          type="number"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Select
                  value={incomeCurrency}
                  onChange={handleCurrencyChange}
                  size="small"
                  sx={{
                    '& .MuiSelect-select': {
                      padding: '4px 8px',
                      fontSize: '0.875rem',
                      minWidth: '70px'
                    },
                    '& fieldset': { border: 'none' }
                  }}
                >
                  <MenuItem value="PKR">₨ PKR</MenuItem>
                  <MenuItem value="USD">$ USD</MenuItem>
                  <MenuItem value="EUR">€ EUR</MenuItem>
                  <MenuItem value="GBP">£ GBP</MenuItem>
                  <MenuItem value="SAR">﷼ SAR</MenuItem>
                  <MenuItem value="AED">د.إ AED</MenuItem>
                  <MenuItem value="INR">₹ INR</MenuItem>
                </Select>
              </InputAdornment>
            ),
            inputProps: { min: 0 }
          }}
        />
      </Grid>

      {/* Work Location Field */}
      <Grid item xs={12}>
        <TextInput
          label="Work Location"
          value={formData.profession?.work_location || ""}
          onChange={(e) => handleInputChange("profession.work_location", e.target.value)}
          placeholder="Enter your work location/city"
          fullWidth
        />
      </Grid>
    </Grid>
  );
};

export default EducationProfessionStep;